import {
  IconArrowRight,
  IconBuildingHospital,
  IconCalendarEvent,
  IconChartBar,
  IconCircleCheck,
  IconCloudOff,
  IconFileInvoice,
  IconFileText,
  IconHandFinger,
  IconHelpCircle,
  IconLanguage,
  IconListCheck,
  IconMicrophone,
  IconPhotoScan,
  IconShieldCheck,
  IconStethoscope,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { LanguageSwitch, Logo } from '@/components/common';
import { navigate } from '@/lib/router';
import { useAuth } from '../auth';
import { LiveDemo } from './LiveDemo';
import { ToothMapShowcase } from './Showcases';
import './landing.css';

const DEMO_URL = 'https://studiopresent.com/start';
const STUDIO_URL = 'https://studiopresent.com';

const list = (value: unknown): string[] => (Array.isArray(value) ? value : []);
type Card = { title: string; body: string };

function SignInButton({ className = 'btn' }: { className?: string }) {
  const { t } = useTranslation('landing');
  return (
    <button type="button" className={className} onClick={() => navigate({ name: 'login' })}>
      {t('nav.signIn')}
    </button>
  );
}

function NewTab() {
  const { t } = useTranslation('landing');
  return <span className="visually-hidden"> {t('nav.newTab')}</span>;
}

/** "Studio Present" inside the footer sentence (its translation marks it <studio>). */
function StudioLink({ children }: { children?: ReactNode }) {
  return (
    <a href={STUDIO_URL} target="_blank" rel="noopener">
      {children}
      <NewTab />
    </a>
  );
}

function DemoLink({ label }: { label: string }) {
  return (
    <a className="btn btn--primary" href={DEMO_URL} target="_blank" rel="noopener">
      {label} <IconArrowRight size={18} aria-hidden="true" />
      <NewTab />
    </a>
  );
}

function Nav() {
  const { t } = useTranslation('landing');
  const { changeLanguage } = useAuth();
  return (
    <header className="landing__nav">
      <a className="landing__logo" href="/">
        <Logo size={32} />
      </a>
      <div className="landing__nav-actions">
        <LanguageSwitch onChange={changeLanguage} />
        <SignInButton className="btn btn--sm" />
        <a
          className="btn btn--sm btn--primary landing__nav-demo"
          href={DEMO_URL}
          target="_blank"
          rel="noopener"
        >
          {t('nav.demo')}
          <NewTab />
        </a>
      </div>
    </header>
  );
}

function Hero() {
  const { t } = useTranslation('landing');
  return (
    <section className="landing__hero">
      <div className="landing__hero-text">
        <p className="landing__eyebrow">{t('hero.eyebrow')}</p>
        <h1>{t('hero.title')}</h1>
        <p className="landing__lead">{t('hero.lead')}</p>
        <ul className="landing__tags">
          {list(t('hero.tags', { returnObjects: true })).map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </ul>
        <div className="landing__ctas">
          <DemoLink label={t('nav.demo')} />
          <SignInButton />
        </div>
      </div>
      <LiveDemo />
    </section>
  );
}

function Block({
  title,
  body,
  children,
  flip = false,
}: {
  title: string;
  body: string;
  children: ReactNode;
  flip?: boolean;
}) {
  return (
    <article className={`showcase ${flip ? 'showcase--flip' : ''}`}>
      <div className="showcase__text">
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
      {children}
    </article>
  );
}

const CHAIR_ICONS = [IconHandFinger, IconHelpCircle, IconCloudOff];

function Showcase() {
  const { t } = useTranslation('landing');
  return (
    <section className="landing__section" id="demo">
      <p className="landing__label">{t('showcase.label')}</p>
      <h2>{t('showcase.title')}</h2>
      <Block title={t('showcase.chair.title')} body={t('showcase.chair.body')}>
        <ul className="showcase__points">
          {list(t('showcase.chair.points', { returnObjects: true })).map((point, i) => {
            const Icon = CHAIR_ICONS[i] ?? IconMicrophone;
            return (
              <li key={point}>
                <Icon size={22} />
                {point}
              </li>
            );
          })}
        </ul>
      </Block>
      <Block title={t('showcase.map.title')} body={t('showcase.map.body')} flip>
        <ToothMapShowcase />
      </Block>
    </section>
  );
}

/** In the order of the cards in landing.json → features.cards. */
const FEATURE_ICONS = [
  IconCalendarEvent,
  IconFileInvoice,
  IconPhotoScan,
  IconStethoscope,
  IconCircleCheck,
  IconChartBar,
  IconFileText,
  IconBuildingHospital,
];

/** What the app does beyond dictation: calendar, offers, X-rays, diagnoses, reports… */
function Features() {
  const { t } = useTranslation('landing');
  const cards = t('features.cards', { returnObjects: true }) as Card[];
  return (
    <section className="landing__section" id="features">
      <p className="landing__label">{t('features.label')}</p>
      <h2>{t('features.title')}</h2>
      <div className="features">
        {(Array.isArray(cards) ? cards : []).map((card, i) => {
          const Icon = FEATURE_ICONS[i] ?? IconCircleCheck;
          return (
            <article key={card.title} className="features__card">
              <span className="features__icon">
                <Icon size={24} aria-hidden="true" />
              </span>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Change() {
  const { t } = useTranslation('landing');
  return (
    <section className="landing__section">
      <p className="landing__label">{t('change.label')}</p>
      <h2>{t('change.title')}</h2>
      <div className="change">
        {(['before', 'now'] as const).map((side) => (
          <div key={side} className={`change__col change__col--${side}`}>
            <h3>{t(`change.${side}.title`)}</h3>
            <ul>
              {list(t(`change.${side}.items`, { returnObjects: true })).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

const TRUST_ICONS = [IconListCheck, IconShieldCheck, IconHandFinger, IconLanguage];

function Trust() {
  const { t } = useTranslation('landing');
  const cards = t('trust.cards', { returnObjects: true }) as Card[];
  return (
    <section className="landing__section">
      <p className="landing__label">{t('trust.label')}</p>
      <h2>{t('trust.title')}</h2>
      <div className="trust">
        {(Array.isArray(cards) ? cards : []).map((card, i) => {
          const Icon = TRUST_ICONS[i] ?? IconShieldCheck;
          return (
            <article key={card.title} className="trust__card">
              <span className="trust__num">{String(i + 1).padStart(2, '0')}</span>
              <Icon size={26} className="trust__icon" />
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function LandingPage() {
  const { t } = useTranslation('landing');
  return (
    <div className="landing">
      <Nav />
      <main>
        <Hero />
        <Showcase />
        <Features />
        <Change />
        <Trust />
        <section className="landing__result">
          <p className="landing__label">{t('result.label')}</p>
          <h2>{t('result.title')}</h2>
          <p>{t('result.body')}</p>
        </section>
        <section className="landing__closing">
          <h2>{t('closing.title')}</h2>
          <DemoLink label={t('closing.cta')} />
        </section>
      </main>
      <footer className="landing__footer">
        <span>
          <Trans t={t} i18nKey="footer.made" components={{ studio: <StudioLink /> }} />
        </span>
      </footer>
    </div>
  );
}
